import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Button, Form, Alert, Card, Modal } from 'react-bootstrap';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import GroupSelectionModal from '../../Common/GroupSelectionModal';
import { isSuperAdmin, isOperationsAdmin } from '../../utils/roleBasedAccess';
import '../../pages/ManageTournaments/ManageTournaments.css';

// Dummy data for teams and venues
const DUMMY_TEAMS = {
  '1': [
    { id: 't1', name: 'Mumbai United', abbreviation: 'MU' },
    { id: 't2', name: 'Pune Warriors', abbreviation: 'PW' },
    { id: 't3', name: 'Delhi Dynamos', abbreviation: 'DD' },
    { id: 't4', name: 'Chennai Super Kings', abbreviation: 'CSK' },
    { id: 't5', name: 'Bengaluru FC', abbreviation: 'BFC' }
  ]
};

const DUMMY_VENUES = [
  { id: 'v1', name: 'Mumbai Stadium' },
  { id: 'v2', name: 'Pune Sports Complex' },
  { id: 'v3', name: 'Delhi Football Ground' },
  { id: 'v4', name: 'Chennai Sports Center' },
  { id: 'v5', name: 'Bengaluru Sports Hub' }
];

const DUMMY_GROUPS = {
  '1': [
    { id: 'g1', name: 'Group A', teamCount: 5 },
    { id: 'g2', name: 'Group B', teamCount: 5 },
    { id: 'g3', name: 'Group C', teamCount: 5 }
  ]
};

const DUMMY_FIXTURE_DETAILS = {
  '1': {
    id: '1',
    tournamentName: 'SFL Football League Mumbai Edition',
    ageCategory: 'U8(Co-Ed)',
    tournamentType: 'Group + Knockout'
  }
};

interface FixtureForm {
  id: string;
  teamA: string;
  teamB: string;
  date: string;
  time: string;
  venue: string;
  matchType: 'League' | 'Knockout';
}

const AddIndividualFixturesPage: React.FC = () => {
  const { fixtureId } = useParams<{ fixtureId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);

  // State management
  const [fixtureData, setFixtureData] = useState<any>(null);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [groups, setGroups] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [fixtures, setFixtures] = useState<FixtureForm[]>([
    {
      id: '1',
      teamA: '',
      teamB: '',
      date: '',
      time: '',
      venue: '',
      matchType: 'League'
    }
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);

  // Check if user has access
  const userData = { ...user, subRole: role, permissions: permissions };
  const isSuperAdminUser = isSuperAdmin(userData);
  const isOperationsAdminUser = isOperationsAdmin(userData);

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Dashboard', onClick: () => navigate('/dashboard') },
    { label: 'Manage Tournaments', onClick: () => navigate('/fixtures') },
    { label: 'Fixture Details', onClick: () => navigate(`/fixtures/${fixtureId}`) },
    { label: 'Add Fixtures', active: true }
  ];

  useEffect(() => {
    if (!isSuperAdminUser && !isOperationsAdminUser) {
      setLoading(false);
      return;
    }

    // Load fixture data
    const loadData = () => {
      try {
        setLoading(true);
        setError(null);
        
        // Simulate API call
        setTimeout(() => {
          const fixture = DUMMY_FIXTURE_DETAILS[fixtureId as keyof typeof DUMMY_FIXTURE_DETAILS];
          const fixtureGroups = DUMMY_GROUPS[fixtureId as keyof typeof DUMMY_GROUPS];
          const fixtureTeams = DUMMY_TEAMS[fixtureId as keyof typeof DUMMY_TEAMS];
          
          if (fixture) {
            setFixtureData(fixture);
            setGroups(fixtureGroups || []);
            setTeams(fixtureTeams || []);
            
            // Check if group is selected from navigation state
            if (location.state?.selectedGroup) {
              setSelectedGroup(location.state.selectedGroup);
            } else {
              setShowGroupModal(true);
            }
          } else {
            setError('Fixture not found');
          }
          setLoading(false);
        }, 500);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load data');
        setLoading(false);
      }
    };

    loadData();
  }, [fixtureId, isSuperAdminUser, isOperationsAdminUser, location.state]);

  // Handle group selection
  const handleGroupSelect = (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    setSelectedGroup(group);
    setShowGroupModal(false);
  };

  // Handle fixture form changes
  const handleFixtureChange = (fixtureId: string, field: string, value: string) => {
    setFixtures(prev => prev.map(fixture => 
      fixture.id === fixtureId 
        ? { ...fixture, [field]: value }
        : fixture
    ));
  };

  // Add new fixture form
  const handleAddFixture = () => {
    const newFixture: FixtureForm = {
      id: (fixtures.length + 1).toString(),
      teamA: '',
      teamB: '',
      date: '',
      time: '',
      venue: '',
      matchType: 'League'
    };
    setFixtures(prev => [...prev, newFixture]);
  };

  // Remove fixture form
  const handleRemoveFixture = (fixtureId: string) => {
    if (fixtures.length > 1) {
      setFixtures(prev => prev.filter(fixture => fixture.id !== fixtureId));
    }
  };

  // Validate fixtures
  const validateFixtures = () => {
    const errors: string[] = [];
    
    fixtures.forEach((fixture, index) => {
      if (!fixture.teamA) errors.push(`Fixture ${index + 1}: Team A is required`);
      if (!fixture.teamB) errors.push(`Fixture ${index + 1}: Team B is required`);
      if (fixture.teamA === fixture.teamB) errors.push(`Fixture ${index + 1}: Team A and Team B cannot be the same`);
      if (!fixture.date) errors.push(`Fixture ${index + 1}: Date is required`);
      if (!fixture.time) errors.push(`Fixture ${index + 1}: Time is required`);
      if (!fixture.venue) errors.push(`Fixture ${index + 1}: Venue is required`);
    });

    return errors;
  };

  // Handle save fixtures
  const handleSaveFixtures = async () => {
    const errors = validateFixtures();
    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      console.log('Saving fixtures:', {
        fixtureId,
        groupId: selectedGroup.id,
        fixtures
      });

      setSuccess(`${fixtures.length} fixture(s) saved successfully!`);
      
      // Navigate back after success
      setTimeout(() => {
        navigate(`/fixtures/${fixtureId}`, { state: { activeTab: 'fixtures' } });
      }, 1500);

    } catch (error) {
      console.error('Error saving fixtures:', error);
      setError('Failed to save fixtures. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    navigate(`/fixtures/${fixtureId}`, { state: { activeTab: 'teams' } });
  };

  // Access denied for non-admin users
  if (!isSuperAdminUser && !isOperationsAdminUser) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <h3>Access Denied</h3>
            <p>You don't have permission to access this page.</p>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumbs */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs items={breadcrumbItems} />
          </div>

          {/* Header Shimmer */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '24px',
            padding: '0 15px'
          }}>
            <div className="shimmer-image" style={{ width: '40px', height: '40px', borderRadius: '8px' }}></div>
            <div className="shimmer-image" style={{ width: '60px', height: '60px', borderRadius: '12px' }}></div>
            <div style={{ flex: 1 }}>
              <div className="shimmer-text" style={{ width: '250px', height: '24px', marginBottom: '8px' }}></div>
              <div className="shimmer-badge" style={{ width: '180px', height: '28px', borderRadius: '12px' }}></div>
            </div>
            <div className="shimmer-button" style={{ width: '120px', height: '40px', borderRadius: '8px' }}></div>
          </div>

          {/* Form Shimmer */}
          <Row>
            <Col>
              <div className="tournament-table-wrapper">
                <ShimmerLoader type="form" />
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error && !fixtureData) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <h3 className="text-danger">Error</h3>
            <p>{error}</p>
            <Button variant="primary" onClick={handleBack}>
              Back to Fixture Details
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '24px',
          padding: '0 15px'
        }}>
          <button 
            className="back-button"
            onClick={handleBack}
            style={{ position: 'relative', zIndex: 10 }}
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #e9ecef'
          }}>
            <i className="ri-calendar-check-line" style={{ fontSize: '24px', color: '#6c757d' }}></i>
          </div>
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#212529',
              margin: 0,
              lineHeight: '1.2'
            }}>
              {fixtureData?.tournamentName} {selectedGroup?.name} Fixtures
            </h1>
            <span style={{
              fontSize: '11px',
              fontWeight: '500',
              padding: '6px 10px',
              borderRadius: '12px',
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              border: '1px solid #bbdefb',
              minWidth: '68px',
              height: '28px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              lineHeight: '1',
              margin: '0'
            }}>
              {fixtureData?.ageCategory}
            </span>
          </div>
          <div>
            <Button
              variant="outline-primary"
              onClick={() => setShowExcelModal(true)}
              className="me-2"
            >
              <i className="ri-file-excel-line me-1"></i>
              Excel Upload
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveFixtures}
              disabled={saving || !selectedGroup}
            >
              {saving ? 'Saving...' : 'Save All Fixtures'}
            </Button>
          </div>
        </div>

        <Row>
          <Col>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            {!selectedGroup ? (
              <Card className="shadow-sm">
                <Card.Body className="text-center py-5">
                  <h4>Select a Group</h4>
                  <p className="text-muted">Please select a group to add fixtures for.</p>
                  <Button variant="primary" onClick={() => setShowGroupModal(true)}>
                    Select Group
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <div>
                {fixtures.map((fixture, index) => (
                  <Card key={fixture.id} className="shadow-sm mb-4">
                    <Card.Header className="bg-white border-bottom">
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Fixture {index + 1}</h5>
                        {fixtures.length > 1 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveFixture(fixture.id)}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </Button>
                        )}
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Select Team A</Form.Label>
                            <Form.Select
                              value={fixture.teamA}
                              onChange={(e) => handleFixtureChange(fixture.id, 'teamA', e.target.value)}
                            >
                              <option value="">Select Team A</option>
                              {teams.map(team => (
                                <option key={team.id} value={team.id}>
                                  {team.name} ({team.abbreviation})
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Select Team B</Form.Label>
                            <Form.Select
                              value={fixture.teamB}
                              onChange={(e) => handleFixtureChange(fixture.id, 'teamB', e.target.value)}
                            >
                              <option value="">Select Team B</option>
                              {teams.map(team => (
                                <option key={team.id} value={team.id}>
                                  {team.name} ({team.abbreviation})
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Date & Time</Form.Label>
                            <Row>
                              <Col md={6}>
                                <Form.Control
                                  type="date"
                                  value={fixture.date}
                                  onChange={(e) => handleFixtureChange(fixture.id, 'date', e.target.value)}
                                />
                              </Col>
                              <Col md={6}>
                                <Form.Control
                                  type="time"
                                  value={fixture.time}
                                  onChange={(e) => handleFixtureChange(fixture.id, 'time', e.target.value)}
                                />
                              </Col>
                            </Row>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Venue</Form.Label>
                            <Form.Select
                              value={fixture.venue}
                              onChange={(e) => handleFixtureChange(fixture.id, 'venue', e.target.value)}
                            >
                              <option value="">Select Venue</option>
                              {DUMMY_VENUES.map(venue => (
                                <option key={venue.id} value={venue.id}>
                                  {venue.name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Match Type</Form.Label>
                            <div className="d-flex gap-3">
                              <Form.Check
                                type="radio"
                                label="League"
                                name={`matchType-${fixture.id}`}
                                value="League"
                                checked={fixture.matchType === 'League'}
                                onChange={(e) => handleFixtureChange(fixture.id, 'matchType', e.target.value)}
                              />
                              <Form.Check
                                type="radio"
                                label="Knockout"
                                name={`matchType-${fixture.id}`}
                                value="Knockout"
                                checked={fixture.matchType === 'Knockout'}
                                onChange={(e) => handleFixtureChange(fixture.id, 'matchType', e.target.value)}
                              />
                            </div>
                          </Form.Group>
                        </Col>
                        <Col md={6} className="d-flex align-items-end">
                          <Button
                            variant="primary"
                            onClick={() => {
                              // Save individual fixture logic
                              console.log('Save fixture:', fixture);
                            }}
                            className="ms-auto"
                          >
                            Save +
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}

                <div className="text-center">
                  <Button
                    variant="outline-primary"
                    onClick={handleAddFixture}
                    className="me-2"
                  >
                    <i className="ri-add-line me-1"></i>
                    Add Fixtures +
                  </Button>
                </div>
              </div>
            )}
          </Col>
        </Row>

        {/* Group Selection Modal */}
        <GroupSelectionModal
          show={showGroupModal}
          onHide={() => setShowGroupModal(false)}
          groups={groups}
          onSelectGroup={handleGroupSelect}
          fixtureName={fixtureData?.tournamentName || ''}
        />

        {/* Excel Upload Modal */}
        <Modal show={showExcelModal} onHide={() => setShowExcelModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Upload Fixtures via Excel</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center py-4">
              <i className="ri-file-excel-line" style={{ fontSize: '48px', color: '#28a745' }}></i>
              <h5 className="mt-3">Excel Upload Feature</h5>
              <p className="text-muted">This feature will be implemented in the next phase.</p>
              <p className="text-muted">You can upload an Excel file with fixture data and preview before saving.</p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowExcelModal(false)}>
              Close
            </Button>
            <Button variant="primary" disabled>
              Upload Excel
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default AddIndividualFixturesPage;